import _ from 'underscore';
import React, { useCallback, useMemo, useContext, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { initializeComponentRef } from '@fluentui/react';
import { getStyles } from './styles';
import { DoubleListBoxContext, withDoubleListBoxContext } from './context';
import { Pagination } from './pagination';
import { List } from './list';

const DoubleListBoxUnstyled = ({
  classes,
  className,
  showSelectedItems,
  onSelectionChange,
  isAddSearchBoxEnabled,
  isAddSearchBoxAutoTrigger,
  isAddAllAlwaysEnabled,
  isAddAllAlwaysDisabled,
  addAllButtonText,
  onAddAllCallback,
  isRemoveSearchBoxEnabled,
  isRemoveSearchBoxAutoTrigger,
  isRemoveAllAlwaysEnabled,
  isRemoveAllAlwaysDisabled,
  removeAllButtonText,
  onRemoveAllCallback,
  customRenderBulkControl,
  customRenderItems,
  customRenderSelectedItems,
  customSearchPlaceHolder,
}) => {
  const {
    state: {
      items,
      pageSize,
      selectedItems,
      pageNumber,
      totalCount,
      search,
      selectionSearch,
    },
    dispatch,
  } = useContext(DoubleListBoxContext);
  const isInitialRender = useRef(true);

  const onPageChange = useCallback((page) => {
    dispatch({ type: 'UPDATE_PAGE_NUMBER', payload: page });
  }, [dispatch]);

  const onSearchItems = useCallback(_.debounce((text) => {
    dispatch({ type: 'SET_ITEM_FILTER', payload: text });
  }, 500, false), [dispatch]);

  const onSearchSelectedItems = useCallback(_.debounce((text) => {
    dispatch({ type: 'SET_SELECTED_ITEM_FILTER', payload: text });
  }, 500, false), [dispatch]);

  const onItemDeselect = useCallback((item) => {
    dispatch({ type: 'DESELECT_ITEM', payload: item });
  }, [dispatch]);

  const onItemSelect = useCallback((item) => {
    dispatch({ type: 'SELECT_ITEM', payload: item });
  }, [dispatch]);

  const filteredSelectedItems = useMemo(() => {
    const result = selectionSearch ? selectedItems.filter(item => item.name.toLowerCase().includes(selectionSearch.toLowerCase())) : selectedItems;
    return result;
  }, [selectedItems, selectionSearch]);

  const addAll = useCallback(() => {
    dispatch({ type: 'SELECT_ALL' });
    onAddAllCallback();
  }, [dispatch, onAddAllCallback]);

  const removeAll = useCallback(() => {
    dispatch({ type: 'DESELECT_ITEMS', payload: filteredSelectedItems });
    onRemoveAllCallback();
  }, [dispatch, filteredSelectedItems, onRemoveAllCallback]);

  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
    } else {
      onSelectionChange(selectedItems);
    }
  }, [onSelectionChange, selectedItems]);

  return (
    <div className={classNames(classes.container, 'double-list-box', className)}>
      <div className={classes.lists}>
        <List
          items={items}
          onSearch={onSearchItems}
          onItemSelect={onItemSelect}
          onItemDeselect={onItemDeselect}
          isFiltered={!_.isEmpty(search)}
          onBulkOperation={addAll}
          defaultItemCount={pageSize}
          isSearchBoxEnabled={isAddSearchBoxEnabled}
          isSearchBoxAutoTrigger={isAddSearchBoxAutoTrigger}
          customizedActionText={addAllButtonText}
          isActionAlwaysEnabled={isAddAllAlwaysEnabled}
          isActionAlwaysDisabled={isAddAllAlwaysDisabled}
          isPaginationEnabled={!!pageSize}
          customRenderBulkControl={customRenderBulkControl}
          customRenderItems={customRenderItems}
          customRenderSelectedItems={customRenderSelectedItems}
          customSearchPlaceHolder={customSearchPlaceHolder}
        />
        {
          showSelectedItems && (
            <List
              items={filteredSelectedItems}
              isSelectedList
              onItemSelect={onItemSelect}
              onItemDeselect={onItemDeselect}
              onSearch={onSearchSelectedItems}
              onBulkOperation={removeAll}
              isFiltered={!_.isEmpty(selectionSearch)}
              isSearchBoxEnabled={isRemoveSearchBoxEnabled}
              isSearchBoxAutoTrigger={isRemoveSearchBoxAutoTrigger}
              customizedActionText={removeAllButtonText}
              isActionAlwaysEnabled={isRemoveAllAlwaysEnabled}
              isActionAlwaysDisabled={isRemoveAllAlwaysDisabled}
              customRenderBulkControl={customRenderBulkControl}
              customRenderItems={customRenderItems}
              customRenderSelectedItems={customRenderSelectedItems}
            />
          )
        }
      </div>
      {pageSize && <Pagination
        pageNumber={pageNumber}
        pageSize={pageSize}
        total={totalCount}
        onPageChange={onPageChange}
      />}
    </div>
  );
};

DoubleListBoxUnstyled.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.shape(_.mapObject(
    getStyles({}, {}),
    _.constant(PropTypes.string.isRequired)
  )).isRequired,
  showSelectedItems: PropTypes.bool,
  onSelectionChange: PropTypes.func,
  isAddSearchBoxEnabled: PropTypes.bool,
  isAddSearchBoxAutoTrigger: PropTypes.bool,
  isAddAllAlwaysEnabled: PropTypes.bool,
  isAddAllAlwaysDisabled: PropTypes.bool,
  addAllButtonText: PropTypes.string,
  onAddAllCallback: PropTypes.func,
  isRemoveSearchBoxEnabled: PropTypes.bool,
  isRemoveSearchBoxAutoTrigger: PropTypes.bool,
  isRemoveAllAlwaysEnabled: PropTypes.bool,
  isRemoveAllAlwaysDisabled: PropTypes.bool,
  removeAllButtonText: PropTypes.string,
  onRemoveAllCallback: PropTypes.func,
  customRenderBulkControl: PropTypes.func,
  customRenderItems: PropTypes.func,
  customRenderSelectedItems: PropTypes.func,
  customSearchPlaceHolder: PropTypes.string,
};

DoubleListBoxUnstyled.defaultProps = {
  className: '',
  showSelectedItems: true,
  onSelectionChange: () => {},
  isAddSearchBoxEnabled: true,
  isAddSearchBoxAutoTrigger: true,
  isAddAllAlwaysEnabled: false,
  isAddAllAlwaysDisabled: false,
  addAllButtonText: null,
  onAddAllCallback: _.noop,
  isRemoveSearchBoxEnabled: true,
  isRemoveSearchBoxAutoTrigger: true,
  isRemoveAllAlwaysEnabled: false,
  isRemoveAllAlwaysDisabled: false,
  removeAllButtonText: null,
  onRemoveAllCallback: _.noop,
  customRenderBulkControl: null,
  customRenderItems: null,
  customRenderSelectedItems: null,
  customSearchPlaceHolder: '',
};

class DoubleListBoxWithComponentRef extends React.Component {
  constructor(props) {
    super(props);
    initializeComponentRef(this);
  }

  setSelectedItems = (selection) => {
    this.props.context.dispatch({ type: 'SET_SELECTED_ITEMS', payload: selection });
  };

  render() {
    return <DoubleListBoxUnstyled {...this.props} />;
  }
}

DoubleListBoxWithComponentRef.propTypes = {
  context: PropTypes.shape({
    dispatch: PropTypes.func.isRequired,
  }).isRequired,
};

export const DoubleListBox = withDefaultStyles(withDoubleListBoxContext(DoubleListBoxWithComponentRef), getStyles);
